import React, { useEffect, useRef, useState, ReactNode } from 'react';
import styled from 'styled-components';

interface StickyHeaderProps {
  children: ReactNode;
  top?: number;
  noWrapper?: boolean;
  zIndex?: number;
  noWrap?: boolean;
  disableStickyBelow?: number;
}

const StyledStickyWrapper = styled.div<{
  $top: number;
  $isStuck: boolean;
  $noWrapper?: boolean;
  $zIndex: number;
  $disableStickyBelow?: number;
}>`
  position: sticky;
  top: ${({ $top }) => `${$top}px`};
  z-index: ${({ $zIndex }) => $zIndex};
  width: 100%;

  ${({ $isStuck, $noWrapper, theme }) =>
    $isStuck &&
    !$noWrapper &&
    `
    width: calc(100% + ${theme.spacing.medium} * 2);
    margin-left: -${theme.spacing.medium};
    margin-right: -${theme.spacing.medium};
    padding: ${theme.spacing.medium};
    border: 1px solid ${theme.palette.border};
    border-radius: ${theme.radius.small};
    background-color: ${theme.palette.white};
  `}

  ${({ $disableStickyBelow }) =>
    $disableStickyBelow &&
    `
    @media (max-width: ${$disableStickyBelow}px) {
      position: static;
    }
  `}
`;

export const StickyHeader: React.FC<StickyHeaderProps> = ({
  children,
  top = 120,
  noWrapper,
  zIndex = 1,
  noWrap = false,
  disableStickyBelow,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const handleScroll = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      setIsStuck(rect.top <= top);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [top]);

  if (noWrap) {
    return <>{children}</>;
  }

  return (
    <StyledStickyWrapper
      ref={wrapperRef}
      $top={top}
      $isStuck={isStuck}
      $noWrapper={noWrapper}
      $zIndex={zIndex}
      $disableStickyBelow={disableStickyBelow}
      className={isStuck ? 'is-stuck' : undefined}
    >
      {children}
    </StyledStickyWrapper>
  );
};
